import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment-timezone'

import { 
    useGetFormularioByIdQuery,
    useUpdateFormularioSuscripcionMutation 
} from '../../../../store/api/endpoints/formularioSuscripcionApiSlice'

import { openFeedback } from '../../../../store/slices/feedback'

import HeaderVista from '../../../elementos/HeaderVista'
import Input from '../../../elementos/Input'
import Loading from '../../../elementos/Loading'

import Button from '@mui/material/Button'
import { Breadcrumbs, Divider, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'

import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import CheckIcon from '@mui/icons-material/Check'
import TitleIcon from '@mui/icons-material/Title'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'

export default function FormularioSuscripcionEditar() {

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { id } = useParams() // Capturamos el ID de la URL

    // Estados locales para los inputs
    const [titulo, setTitulo] = useState('')
    const [desde, setDesde] = useState('')
    const [hasta, setHasta] = useState('')
    const [importe, setImporte] = useState('')

    // 1. Hook de Lectura: Trae los datos iniciales
    const {
        data: formularioData,
        isLoading: isLoadingGet,
        isSuccess: isSuccessGet,
        isError: isErrorGet
    } = useGetFormularioByIdQuery(id)

    // 2. Hook de Mutación: Para enviar los cambios
    const [
        actualizarRegistro,
        {
            isLoading: isLoadingUpdate,
            isSuccess: isSuccessUpdate,
            isError: isErrorUpdate,
            error: errorDataUpdate,
        }
    ] = useUpdateFormularioSuscripcionMutation()


    // Efecto para popular el formulario cuando los datos se cargan exitosamente
    useEffect(() => {
        if (isSuccessGet && formularioData) {
            setTitulo(formularioData.titulo)
            setImporte(formularioData.importe)

            // Convertimos el ISO del backend a la hora de Buenos Aires,
            // dándole el formato exacto que necesita el input datetime-local
            const formatoInput = 'YYYY-MM-DDTHH:mm'
            
            if (formularioData.desde) {
                setDesde(moment(formularioData.desde).tz("America/Argentina/Buenos_Aires").format(formatoInput))
            }
            if (formularioData.hasta) {
                setHasta(moment(formularioData.hasta).tz("America/Argentina/Buenos_Aires").format(formatoInput))
            }
        }
    }, [isSuccessGet, formularioData])

    // Manejo de errores de carga inicial
    useEffect(() => {
        if (isErrorGet) {
            dispatch(openFeedback({
                severity: 'error',
                message: 'No se pudo cargar la información del registro.'
            }))
            navigate("/formularios/formulario-suscripcion")
        }
    }, [isErrorGet, navigate, dispatch])


    const handleSubmit = (e) => {
        e.preventDefault()

        // Hacemos el proceso inverso: tomamos el valor local del input y lo enviamos como ISO
        const body = {
            id, // Importante inyectar el ID aquí para que el slice arme la URL correcta del PUT
            titulo,
            importe: Number(importe), 
            desde: moment.tz(desde, "America/Argentina/Buenos_Aires").toISOString(),
            hasta: moment.tz(hasta, "America/Argentina/Buenos_Aires").toISOString()
        }
        
        actualizarRegistro(body)
    }

    // Feedback de actualización
    useEffect(() => {
        if (isErrorUpdate) {
            dispatch(openFeedback({
                severity: 'error',
                message: errorDataUpdate?.data?.message || 'Ocurrió un error al actualizar el formulario'
            }))
        }
    }, [isErrorUpdate, errorDataUpdate, dispatch])

    useEffect(() => {
        if (isSuccessUpdate) {
            dispatch(openFeedback({
                severity: 'success',
                message: "¡Suscripción actualizada con éxito!"
            }))
            navigate("/formularios/formulario-suscripcion") 
        }
    }, [isSuccessUpdate, navigate, dispatch])


    // Si estamos cargando los datos iniciales o guardando, mostramos el Loading
    if (isLoadingGet || isLoadingUpdate) return (<Loading />)

    return (
        <>
            <HeaderVista
                titulo={
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link to="/formularios/formulario-suscripcion">
                            <Typography color="text.primary">Suscripciones</Typography>
                        </Link>
                        <Typography color="text.primary">Editar</Typography>
                    </Breadcrumbs>
                }
                icon={<InventoryOutlinedIcon />} // Cambiamos el ícono del header a edición
            />
            <form onSubmit={handleSubmit}>
                <Grid container rowSpacing={2} columnSpacing={2}>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Input
                            label="Título"
                            icon={<TitleIcon />}
                            value={titulo}
                            onChange={setTitulo}
                            type="text"
                            required
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Input
                            label="Importe"
                            icon={<AttachMoneyIcon />}
                            value={importe}
                            onChange={setImporte}
                            type="number"
                            required
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Input
                            label="Desde (Fecha y Hora)"
                            icon={<EventAvailableIcon />}
                            value={desde}
                            onChange={setDesde}
                            type="datetime-local"
                            required
                        />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Input
                            label="Hasta (Fecha y Hora)"
                            icon={<EventBusyIcon />}
                            value={hasta}
                            onChange={setHasta}
                            type="datetime-local"
                            required
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Button type="submit" variant="contained" color="success" startIcon={<CheckIcon />} >
                            Guardar Cambios
                        </Button>
                    </Grid>

                </Grid>
            </form>
        </>
    )
}